import {
  ComponentMultiStyleConfig,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { CommentBlockItem } from "../CommentBlockItem/CommentBlockItem";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import { ColorScheme } from "../../../lib/styles/types/color-scheme";
import { CMCommentBlockInput } from "../CMCommentBlockInput/CMCommentBlockInput";

export type CommentBlockProps = {
  mainComment: Comment;
  replyComments: Comment[];

  photoPlaceholderUrl: string | undefined;
  formatPostedAt: (datetime: Date) => string;

  onSubmit: (values: FormValues) => void;
  inputPlaceholder: string | { shrunk: string; expanded: string } | undefined;
  fileUploadLabel: string;

  submitButton: {
    label: string;
    size: string;
    colorScheme: ColorScheme;
  };
  cancelButton: {
    label: string;
    size: string;
    colorScheme: ColorScheme;
  };
} & ComponentStyleProps;

export const CommentBlock = ({
  mainComment,
  replyComments,

  photoPlaceholderUrl,
  formatPostedAt,

  onSubmit,
  inputPlaceholder,
  fileUploadLabel,

  submitButton,
  cancelButton,

  ...rest
}: CommentBlockProps) => {
  const styles = useMultiStyleConfig("CommentBlockStyles", {});

  return (
    <VStack alignItems="stretch" {...rest} __css={styles.container}>
      <VStack alignItems="stretch" __css={styles.mainComment}>
        <CommentBlockItem
          id={mainComment.id}
          userId={mainComment.userId}
          userName={mainComment.userName}
          photoUrl={mainComment.photoUrl}
          postedAt={mainComment.postedAt}
          content={mainComment.content}
          photoPlaceholderUrl={photoPlaceholderUrl}
          formatPostedAt={formatPostedAt}
        />
      </VStack>
      <VStack alignItems="flex-start" __css={styles.replyComments}>
        {replyComments.map((c) => (
          <CommentBlockItem
            key={c.id}
            id={c.id}
            userId={c.userId}
            userName={c.userName}
            photoUrl={c.photoUrl}
            postedAt={c.postedAt}
            content={c.content}
            photoPlaceholderUrl={photoPlaceholderUrl}
            formatPostedAt={formatPostedAt}
          />
        ))}
      </VStack>
      <CMCommentBlockInput
        onSubmit={onSubmit}
        placeholder={inputPlaceholder}
        fileUploadLabel={fileUploadLabel}
        submitButton={submitButton}
        cancelButton={cancelButton}
        sx={styles.input}
      />
    </VStack>
  );
};

export const CommentBlockStyles: ComponentMultiStyleConfig = {
  parts: ["container", "mainComment", "replyComments", "input"],
  baseStyle: {
    container: {
      p: "4",

      borderWidth: "1px",
      borderColor: "gray.200",
      borderRadius: "lg",
      bgColor: "white",
    },
    mainComment: {
      pb: "4",

      borderBottomWidth: "1px",
      borderColor: "gray.200",
    },
    replyComments: {
      mt: "4",
      pl: "4",

      justifyContent: "flex-start",
      alignItems: "flex-start",
      gap: "4",
    },
    input: {
      mt: "4",
    },
  },
};

type Comment = {
  id: string;
  userId: string;

  photoUrl: string | undefined; // TODO もしかしたら () => Promise<string> も受け取れるようにすべきかも。
  userName: string;
  postedAt: Date;
  content: string;
};

type FormValues = { text: string; files: File[] };
